import type { ContextField } from "@appretio/context-selector";
import { useCallback, useMemo, useState } from "react";

/** Return type of the {@link useContextFilters} hook. */
interface UseContextFiltersReturn {
  /** Current selections keyed by context field. Empty arrays are excluded. */
  selections: Record<string, string[]>;
  /**
   * Non-empty selections suitable for passing to `useCubeContext` as a filter,
   * or `undefined` when nothing is selected.
   */
  activeFilters: Record<string, string[]> | undefined;
  /**
   * Update the selected values for a single field.
   * Passing an empty array removes the field from selections.
   */
  handleFieldChange: (field: ContextField, values: string[]) => void;
  /** Reset all selections to empty. */
  resetSelections: () => void;
}

/**
 * Manages multi-field context selections used across ManageCubes operation
 * components. Derives `activeFilters` (non-empty selections or `undefined`)
 * and provides a setter that auto-cleans empty entries.
 *
 * Accepts an optional `onFieldChange` callback for components that need
 * side-effects when a field changes (e.g. clearing related new-value inputs).
 */
export function useContextFilters(
  onFieldChange?: (field: ContextField, values: string[]) => void
): UseContextFiltersReturn {
  const [selections, setSelections] = useState<Record<string, string[]>>({});

  const activeFilters = useMemo(
    function deriveActiveFilters() {
      const f: Record<string, string[]> = {};
      for (const [key, vals] of Object.entries(selections)) {
        if (vals.length > 0) f[key] = vals;
      }
      return Object.keys(f).length > 0 ? f : undefined;
    },
    [selections]
  );

  const handleFieldChange = useCallback(
    function handleFieldChange(field: ContextField, values: string[]) {
      setSelections(function updateSelections(prev) {
        const next = { ...prev };
        if (values.length === 0) {
          delete next[field];
        } else {
          next[field] = values;
        }
        return next;
      });
      onFieldChange?.(field, values);
    },
    [onFieldChange]
  );

  const resetSelections = useCallback(function resetSelections() {
    setSelections({});
  }, []);

  return { selections, activeFilters, handleFieldChange, resetSelections };
}
